import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import QuantityField from '../QuantityField';
import VaccinationLotPicker, { AUTO_LOT, LotSelection, lotPayload } from './VaccinationLotPicker';
import { inventoryService } from '../../services/inventoryService';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { allocateLots } from './fefo';

export interface BarnVaccineLine {
  name: string;
  horses: Array<{ id: string; name: string }>;
}

type HorseLot = { doseMl: number | null } & ReturnType<typeof lotPayload>;

/** Per vaccine, per horse id: the lot and dose that horse's vaccine line carries. */
export type BarnVaccineLots = Record<string, Record<string, HorseLot>>;

interface BarnVaccineLotsDialogProps {
  open: boolean;
  vaccines: BarnVaccineLine[];
  /** The run's "Deduct stock from" ('' = Automatic). */
  locationId: string;
  /** The run's day (yyyy-MM-dd): an expired lot is judged against it, as the server does. */
  givenOn?: string;
  onConfirm: (lots: BarnVaccineLots) => void;
  /** Bill and record without lot details. */
  onSkip: () => void;
  onCancel: () => void;
}

/**
 * The lots for each vaccine on a barn run (spec §18, ADR-0047): every horse gets its own vaccination record
 * and its own lot, earliest expiry first, moving to the next lot when one runs out. The vet can start from
 * another lot or type one off the vial. The server records each horse's lot and draws its dose from it; a
 * horse no lot covers still saves, flagged.
 */
const BarnVaccineLotsDialog: React.FC<BarnVaccineLotsDialogProps> = ({
  open,
  vaccines,
  locationId,
  givenOn,
  onConfirm,
  onSkip,
  onCancel,
}) => {
  const [perVaccine, setPerVaccine] = useState<BarnVaccineLots>({});
  const [unconfirmed, setUnconfirmed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    setPerVaccine({});
    setUnconfirmed({});
  }, [open]);

  const report = useCallback((name: string, lots: Record<string, HorseLot>) => {
    setPerVaccine((prev) => ({ ...prev, [name]: lots }));
  }, []);
  const reportConfirmation = useCallback((name: string, needs: boolean) => {
    setUnconfirmed((prev) => (prev[name] === needs ? prev : { ...prev, [name]: needs }));
  }, []);

  const blocked = Object.values(unconfirmed).some(Boolean);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Vaccine lots for this run</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each horse gets its own vaccination record. Lots are used earliest expiry first, and the next lot starts
          when one runs out.
        </Typography>
        {vaccines.map((v) => (
          <BarnVaccineSection
            key={v.name}
            line={v}
            locationId={locationId}
            givenOn={givenOn}
            onChange={report}
            onNeedsConfirmation={reportConfirmation}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Back</Button>
        <Button onClick={onSkip}>Skip lots and bill</Button>
        <Button variant="contained" disabled={blocked} onClick={() => onConfirm(perVaccine)}>
          Record and bill
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** One vaccine of the run: the mL per horse, the lot to start from, and which lot each horse gets. */
const BarnVaccineSection: React.FC<{
  line: BarnVaccineLine;
  locationId: string;
  givenOn?: string;
  onChange: (name: string, lots: Record<string, HorseLot>) => void;
  onNeedsConfirmation: (name: string, needs: boolean) => void;
}> = ({ line, locationId, givenOn, onChange, onNeedsConfirmation }) => {
  const tz = useClinicTimezone();
  const [dose, setDose] = useState<number | null>(1);
  const [selection, setSelection] = useState<LotSelection>(AUTO_LOT);
  // The picker's own read (same key), so both see the same balances.
  const { data } = useQuery(
    ['vaccine-lots', line.name, locationId],
    () => inventoryService.getLotsForDispense(line.name, locationId || undefined),
    { staleTime: 30 * 1000, retry: false },
  );
  const lots = useMemo(() => data?.lots ?? [], [data]);
  const doseMl = dose && dose > 0 ? dose : null;
  const allocation = useMemo(
    () =>
      selection.mode === 'manual'
        ? null
        : allocateLots(lots, line.horses.length, doseMl, tz, selection.mode === 'lot' ? selection.lotNumber : undefined),
    [lots, line.horses.length, doseMl, tz, selection],
  );
  const perHorse = useMemo(
    () =>
      Object.fromEntries(
        line.horses.map((h, i) => [
          h.id,
          { doseMl, ...(allocation ? { lotNumber: allocation[i].lot?.lotNumber } : lotPayload(selection)) },
        ]),
      ),
    [line.horses, doseMl, allocation, selection],
  );
  useEffect(() => onChange(line.name, perHorse), [line.name, perHorse, onChange]);
  const reportNeeds = useCallback((needs: boolean) => onNeedsConfirmation(line.name, needs), [line.name, onNeedsConfirmation]);

  const shortCount = allocation?.filter((a) => a.short).length ?? 0;
  const firstLot = allocation?.[0]?.lot;
  const onFirstLot = allocation?.filter((a) => a.lot?.id === firstLot?.id).length ?? line.horses.length;

  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {line.name} · {line.horses.length} {line.horses.length === 1 ? 'horse' : 'horses'}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <QuantityField label="mL per horse" size="small" fullWidth value={dose} onChange={setDose} />
        </Grid>
        <Grid item xs={12} sm={9}>
          <VaccinationLotPicker
            vaccineName={line.name}
            locationId={locationId}
            doseMl={doseMl}
            doseCount={onFirstLot}
            value={selection}
            onChange={setSelection}
            onNeedsConfirmation={reportNeeds}
            givenOn={givenOn}
          />
        </Grid>
      </Grid>

      {allocation && lots.length > 0 && (
        <Table size="small" sx={{ mt: 1.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Horse</TableCell>
              <TableCell>Lot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {line.horses.map((h, i) => (
              <TableRow key={h.id}>
                <TableCell>{h.name}</TableCell>
                <TableCell>
                  {allocation[i].lot?.lotNumber ?? 'No lot'}
                  {allocation[i].short && <Chip size="small" color="warning" label="Not enough left" sx={{ ml: 1 }} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {shortCount > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          No lot has a full dose left for {shortCount} {shortCount === 1 ? 'horse' : 'horses'}. They are still recorded,
          on the last lot, and flagged so the stock can be checked.
        </Alert>
      )}
    </Box>
  );
};

export default BarnVaccineLotsDialog;
